import React, { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { LogOut, TimerReset, Trash2, Eraser } from 'lucide-react'
import axios from 'axios'
import Input from '../components/Input'
import Select from '../components/Select'
import Loading from '../components/Loading'
import * as XLSX from 'xlsx'
import { addUser, getUsers, forceLogoutUser, forceLogoutMultipleUsers, resetLoginRateLimit, deleteUser } from '../actions/userActions'
import { getRoles } from '../actions/roleActions'
import { ADD_USER_RESET, GET_USERS_RESET, FORCE_LOGOUT_USER_RESET, FORCE_LOGOUT_MULTIPLE_RESET, RESET_RATE_LIMIT_RESET, DELETE_USER_RESET } from '../constants/userConstants'

const API = process.env.REACT_APP_API_URL

const inputCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white';
const visibleEmail = email => /@local\.invalid$/i.test(email || '') ? '' : (email || '');

function Modal({ open, onClose, title, children, footer }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>
        <div className="px-6 py-4 overflow-y-auto flex-1">{children}</div>
        {footer && <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">{footer}</div>}
      </div>
    </div>
  );
}

const btnPrimary = 'bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors';
const btnOutline = 'border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium px-4 py-2 rounded-lg transition-colors';
const btnDanger = 'bg-red-500 hover:bg-red-600 disabled:bg-gray-300 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors';
const formatDate = value => value ? new Date(value).toLocaleDateString() : 'N/A';

const UserList = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const [data, setData] = useState([])
  const [roleOptions, setRoleOptions] = useState([])
  const [rolesFromServer, setRolesFromServer] = useState([])

  const [openModal, setOpenModal] = useState(false)
  const [email, setEmail] = useState('')
  const [identifier, setIdentifier] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState('')
  const [ipAddress, setIpAddress] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [passwordModal, setPasswordModal] = useState(false)

  const [forceLogoutModal, setForceLogoutModal] = useState(false)
  const [selectedUserForLogout, setSelectedUserForLogout] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)
  const [selectedUserForDelete, setSelectedUserForDelete] = useState(null)
  const [selectedRows, setSelectedRows] = useState([])
  const [bulkLogoutModal, setBulkLogoutModal] = useState(false)

  const [rateLimitModal, setRateLimitModal] = useState(false)
  const [selectedUserForRateLimit, setSelectedUserForRateLimit] = useState(null)

  const [agentResetModal, setAgentResetModal] = useState(false)
  const [selectedUserForAgentReset, setSelectedUserForAgentReset] = useState(null)
  const [resettingAgent, setResettingAgent] = useState(false)

  const [addUserOption, setAddUserOption] = useState(true)
  const [forceLogoutOption, setForceLogoutOption] = useState(true)
  const [resetRateLimitOption, setResetRateLimitOption] = useState(true)
  const [agentResetOption, setAgentResetOption] = useState(false)
  const [loading, setLoading] = useState(true)

  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState('')
  const [page, setPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(20)

  const [assignModal, setAssignModal] = useState(false)
  const [assignTarget, setAssignTarget] = useState(null) // user being assigned
  const [assignLeadId, setAssignLeadId] = useState('')
  const [assignLoading, setAssignLoading] = useState(false)

  const [bulkAssignModal, setBulkAssignModal] = useState(false)
  const [bulkAssignLeadId, setBulkAssignLeadId] = useState('')
  const [bulkAssignLoading, setBulkAssignLoading] = useState(false)

  const { userInfo } = useSelector(state => state.userLogin) || {}
  const { getRoleData } = useSelector(state => state.getRoleInfo) || {}

  useEffect(() => {
    if (getRoleData) {
      setAddUserOption(getRoleData.usersAdd)
      setForceLogoutOption(getRoleData.forceLogoutUsers)
      setResetRateLimitOption(getRoleData.resetUserRateLimit)
      setAgentResetOption(!!getRoleData.agentProgramReset)
      if (!getRoleData.usersView) {
        toast('Not Authorized', { type: 'error', hideProgressBar: true, autoClose: 2000 })
        navigate('/')
      }
    }
  }, [getRoleData, navigate])

  useEffect(() => { dispatch(getUsers()); dispatch(getRoles()) }, [dispatch])

  useEffect(() => {
    const refreshUsers = () => dispatch(getUsers(search))
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') refreshUsers()
    }

    window.addEventListener('focus', refreshUsers)
    document.addEventListener('visibilitychange', refreshWhenVisible)
    return () => {
      window.removeEventListener('focus', refreshUsers)
      document.removeEventListener('visibilitychange', refreshWhenVisible)
    }
  }, [dispatch, search])

  const { loadingGetUsersInfo, errorGetUsersInfo, getUsersData } = useSelector(state => state.getUsersInfo) || {}
  useEffect(() => {
    dispatch({ type: GET_USERS_RESET })
    if (getUsersData) {
      setLoading(false)
      let usersData = getUsersData.users || getUsersData
      if (Array.isArray(usersData)) setData(usersData)
      else if (usersData && typeof usersData === 'object') {
        const arr = Object.values(usersData).filter(item => Array.isArray(item))
        setData(arr[0] || [])
      } else setData([])
    } else if (errorGetUsersInfo) {
      setLoading(false)
      toast(errorGetUsersInfo, { type: 'error', hideProgressBar: true, autoClose: 2000 })
    }
  }, [getUsersData, errorGetUsersInfo])

  const { getRolesData } = useSelector(state => state.getRolesInfo) || {}
  useEffect(() => { if (Array.isArray(getRolesData)) setRolesFromServer(getRolesData) }, [getRolesData])
  useEffect(() => {
    if (rolesFromServer.length > 0) setRoleOptions(rolesFromServer.map(r => ({ id: r._id, title: r.name })))
  }, [rolesFromServer])

  const { loadingForceLogoutUser, errorForceLogoutUser, forceLogoutUserData } = useSelector(state => state.forceLogoutUserInfo) || {}
  useEffect(() => {
    dispatch({ type: FORCE_LOGOUT_USER_RESET })
    if (forceLogoutUserData) {
      toast(forceLogoutUserData.msg, { type: 'success', hideProgressBar: true, autoClose: 3000 })
      setForceLogoutModal(false)
      setSelectedUserForLogout(null)
      setTimeout(() => dispatch(getUsers()), 1000)
    } else if (errorForceLogoutUser) toast(errorForceLogoutUser, { type: 'error', hideProgressBar: true, autoClose: 2000 })
  }, [forceLogoutUserData, errorForceLogoutUser, dispatch])

  const { loadingForceLogoutMultiple, errorForceLogoutMultiple, forceLogoutMultipleData } = useSelector(state => state.forceLogoutMultipleInfo) || {}
  useEffect(() => {
    dispatch({ type: FORCE_LOGOUT_MULTIPLE_RESET })
    if (forceLogoutMultipleData) {
      toast(forceLogoutMultipleData.msg, { type: 'success', hideProgressBar: true, autoClose: 3000 })
      setSelectedRows([])
      setBulkLogoutModal(false)
      setTimeout(() => dispatch(getUsers()), 1000)
    } else if (errorForceLogoutMultiple) toast(errorForceLogoutMultiple, { type: 'error', hideProgressBar: true, autoClose: 2000 })
  }, [forceLogoutMultipleData, errorForceLogoutMultiple, dispatch])

  const { loadingResetRateLimit, errorResetRateLimit, resetRateLimitData } = useSelector(state => state.resetRateLimitInfo) || {}
  useEffect(() => {
    dispatch({ type: RESET_RATE_LIMIT_RESET })
    if (resetRateLimitData) {
      toast(resetRateLimitData.msg, { type: 'success', hideProgressBar: true, autoClose: 3000 })
      setRateLimitModal(false)
      setSelectedUserForRateLimit(null)
    } else if (errorResetRateLimit) toast(errorResetRateLimit, { type: 'error', hideProgressBar: true, autoClose: 2000 })
  }, [resetRateLimitData, errorResetRateLimit, dispatch])

  const { loadingDeleteUser, errorDeleteUser, deleteUserData } = useSelector(state => state.deleteUserInfo) || {}
  useEffect(() => {
    dispatch({ type: DELETE_USER_RESET })
    if (deleteUserData) {
      toast(deleteUserData.msg || 'User deleted successfully', { type: 'success', hideProgressBar: true, autoClose: 2000 })
      setDeleteModal(false)
      setSelectedUserForDelete(null)
      setSelectedRows(prev => prev.filter(u => u._id !== deleteUserData.userId))
      dispatch(getUsers(search))
    } else if (errorDeleteUser) {
      toast(errorDeleteUser, { type: 'error', hideProgressBar: true, autoClose: 2000 })
    }
  }, [deleteUserData, errorDeleteUser, dispatch, search])

  const { loadingAddUserInfo, errorAddUserInfo, addUserData } = useSelector(state => state.addUserInfo) || {}
  useEffect(() => {
    dispatch({ type: ADD_USER_RESET })
    if (addUserData) {
      toast('User added successfully', { type: 'success', hideProgressBar: true, autoClose: 2000 })
      setNewPassword(addUserData)
      setPasswordModal(true)
      setOpenModal(false)
      setEmail(''); setIdentifier(''); setName(''); setRole(''); setIpAddress('')
    } else if (errorAddUserInfo) toast(errorAddUserInfo, { type: 'error', hideProgressBar: true, autoClose: 2000 })
  }, [addUserData, errorAddUserInfo, dispatch])

  const addUserHandler = () => {
    if (!name || !identifier || !email || !role || !ipAddress) {
      toast('All the fields are mandatory', { type: 'error', hideProgressBar: true, autoClose: 2000 })
      return
    }
    dispatch(addUser(name, identifier, email, role, ipAddress))
  }

  const handleSearch = e => {
    setSearch(e.target.value)
    setPage(1)
  }

  const handleRoleFilter = e => {
    setRoleFilter(e.target.value)
    setPage(1)
  }

  const handleRowsPerPage = e => {
    setRowsPerPage(Number(e.target.value))
    setPage(1)
  }

  const toggleRow = (user) => {
    setSelectedRows(prev =>
      prev.find(u => u._id === user._id) ? prev.filter(u => u._id !== user._id) : [...prev, user]
    )
  }

  const filtered = data.filter(u => {
    const matchesSearch = !search || u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase())
    const matchesRole = !roleFilter || u.role?._id === roleFilter
    return matchesSearch && matchesRole
  })
  const selectableFiltered = filtered.filter(u => u._id !== userInfo?.id)
  const allSelected = selectableFiltered.length > 0 && selectableFiltered.every(u => selectedRows.find(r => r._id === u._id))
  const toggleAll = () => {
    if (allSelected) setSelectedRows([])
    else setSelectedRows(data.filter(u => u._id !== userInfo?.id))
  }

  const openAssignModal = (user) => {
    setAssignTarget(user)
    setAssignLeadId(user.assignedTo?._id || user.assignedTo || '')
    setAssignModal(true)
  }

  // Permanently wipes the agent's Agent Program history (leads, points,
  // transactions, vouchers, cached summary). No undo.
  const confirmAgentReset = async () => {
    if (!selectedUserForAgentReset?.merchantId?._id) return
    setResettingAgent(true)
    try {
      await axios.post(
        `${API}/agent-program/agents/${selectedUserForAgentReset.merchantId._id}/reset`,
        {},
        { headers: { Authorization: `Bearer ${userInfo?.token}` } }
      )
      toast.success(`Agent Program progress reset for ${selectedUserForAgentReset.name}.`, {
        hideProgressBar: true,
        autoClose: 2500,
      })
      setAgentResetModal(false)
      setSelectedUserForAgentReset(null)
    } catch (err) {
      toast(err?.response?.data?.message || err.message || 'Reset failed', {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2500,
      })
    } finally {
      setResettingAgent(false)
    }
  }

  const handleAssignLead = async () => {
    if (!assignTarget) return
    setAssignLoading(true)
    try {
      await axios.put(
        `${API}/users/${assignTarget._id}`,
        { assignedTo: assignLeadId || null },
        { headers: { Authorization: `Bearer ${userInfo?.token}` } }
      )
      toast('Lead assigned successfully', { type: 'success', hideProgressBar: true, autoClose: 2000 })
      setAssignModal(false)
      setAssignTarget(null)
      setAssignLeadId('')
      dispatch(getUsers())
    } catch (err) {
      toast(err?.response?.data?.message || 'Failed to assign lead', { type: 'error', hideProgressBar: true, autoClose: 2000 })
    } finally {
      setAssignLoading(false)
    }
  }

  const handleBulkAssignLead = async () => {
    if (!selectedRows.length) return
    setBulkAssignLoading(true)
    try {
      await Promise.all(
        selectedRows.map(u =>
          axios.put(
            `${API}/users/${u._id}`,
            { assignedTo: bulkAssignLeadId || null },
            { headers: { Authorization: `Bearer ${userInfo?.token}` } }
          )
        )
      )
      toast(`Lead assigned to ${selectedRows.length} user(s)`, { type: 'success', hideProgressBar: true, autoClose: 2000 })
      setBulkAssignModal(false)
      setBulkAssignLeadId('')
      setSelectedRows([])
      dispatch(getUsers())
    } catch (err) {
      toast(err?.response?.data?.message || 'Failed to bulk assign lead', { type: 'error', hideProgressBar: true, autoClose: 2000 })
    } finally {
      setBulkAssignLoading(false)
    }
  }

  const totalPages = Math.ceil(filtered.length / rowsPerPage)
  const rows = filtered.slice((page - 1) * rowsPerPage, page * rowsPerPage)

  const exportUsersExcel = () => {
    if (!filtered.length) {
      toast('No users available to export', { type: 'info', hideProgressBar: true, autoClose: 2000 })
      return
    }

    const selectedRole = rolesFromServer.find(r => r._id === roleFilter)
    const exportRows = filtered.map((u, index) => ({
      'S.No': index + 1,
      Name: u.name || '',
      Email: visibleEmail(u.email),
      Identifier: u.identifier || '',
      Role: u.role?.name || 'N/A',
      'Joined On': formatDate(u.joinedOn),
      Status: u.calling ? 'Online' : 'Offline',
      'IP Address': u.ipAddress || '',
    }))
    const worksheet = XLSX.utils.json_to_sheet(exportRows)
    worksheet['!cols'] = [
      { wch: 8 },
      { wch: 24 },
      { wch: 32 },
      { wch: 20 },
      { wch: 20 },
      { wch: 14 },
      { wch: 12 },
      { wch: 18 },
    ]
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Users')
    const roleName = selectedRole?.name ? selectedRole.name.replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '').toLowerCase() : 'all_roles'
    XLSX.writeFile(workbook, `users_${roleName}_${new Date().toISOString().slice(0, 10)}.xlsx`)
  }

  if (loading) return <Loading />

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold text-gray-800">Users</h1>
        <div className="flex gap-2">
          {forceLogoutOption && selectedRows.length > 0 && (
            <>
              <button onClick={() => { setBulkAssignLeadId(''); setBulkAssignModal(true) }} className={btnPrimary}>
                Assign Lead ({selectedRows.length})
              </button>
              <button onClick={() => setBulkLogoutModal(true)} className={btnDanger}>
                Force Logout ({selectedRows.length})
              </button>
            </>
          )}
          {addUserOption && (
            <button onClick={() => setOpenModal(true)} className={btnPrimary}>+ Add User</button>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <input value={search} onChange={handleSearch} placeholder="Search users…" className={`${inputCls} md:w-80`} />
            <select value={roleFilter} onChange={handleRoleFilter} className={`${inputCls} md:w-56`}>
              <option value="">All roles</option>
              {rolesFromServer.map(r => (
                <option key={r._id} value={r._id}>{r.name}</option>
              ))}
            </select>
          </div>
          <button onClick={exportUsersExcel} disabled={!filtered.length} className={btnPrimary}>
            Export Excel
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                {forceLogoutOption && (
                  <th className="px-4 py-3 bg-gray-50 border-b border-gray-200 w-10">
                    <input type="checkbox" checked={allSelected} onChange={toggleAll}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                  </th>
                )}
                {['#', 'Name', 'Email', 'Identifier', 'Role', 'Assigned To', 'Joined On', 'Status', 'Actions'].map(h => (
                  <th key={h} className={`px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200 ${h === 'Actions' ? 'min-w-[220px]' : ''}`}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={forceLogoutOption ? 10 : 9} className="px-4 py-12 text-center text-gray-400 text-sm">No users found</td>
                </tr>
              ) : rows.map((u, index) => (
                <tr key={u._id} className="hover:bg-gray-50 transition-colors">
                  {forceLogoutOption && (
                    <td className="px-4 py-3">
                      {u._id !== userInfo?.id && (
                        <input type="checkbox" checked={!!selectedRows.find(r => r._id === u._id)} onChange={() => toggleRow(u)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                      )}
                    </td>
                  )}
                  <td className="px-4 py-3 text-sm text-gray-500">{(page - 1) * rowsPerPage + index + 1}</td>
                  <td className="px-4 py-3">
                    <Link to={`/users/${u._id}`}
                      className="inline-flex px-3 py-1 bg-green-50 text-green-700 hover:bg-green-100 rounded-lg text-sm font-medium transition-colors">
                      {u.name}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{visibleEmail(u.email)}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{u.identifier}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{u.role?.name || 'N/A'}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {u.assignedTo
                      ? <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">{u.assignedTo.name}</span>
                      : <span className="text-gray-400 text-xs">—</span>
                    }
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{u.joinedOn ? new Date(u.joinedOn).toLocaleDateString() : 'N/A'}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${u.status !== false ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {u.status !== false ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 min-w-[220px]">
                    <div className="flex items-center gap-2 whitespace-nowrap">
                      {forceLogoutOption && (
                        <button
                          onClick={() => openAssignModal(u)}
                          className="text-blue-600 hover:text-blue-800 text-xs font-medium border border-blue-200 hover:border-blue-400 px-3 py-1 rounded-lg transition-colors"
                        >
                          Assign Lead
                        </button>
                      )}
                      {forceLogoutOption && u._id !== userInfo?.id && (
                        <button
                          onClick={() => { setSelectedUserForLogout(u); setForceLogoutModal(true); }}
                          title={`Force logout ${u.name}`}
                          aria-label={`Force logout ${u.name}`}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-red-200 bg-red-50 text-red-600 transition-colors hover:border-red-400 hover:bg-red-100 hover:text-red-700"
                        >
                          <LogOut size={18} />
                        </button>
                      )}
                      {resetRateLimitOption && (
                        <button
                          onClick={() => { setSelectedUserForRateLimit(u); setRateLimitModal(true); }}
                          title={`Reset login rate limit for ${u.name}`}
                          aria-label={`Reset login rate limit for ${u.name}`}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-blue-200 bg-blue-50 text-blue-600 transition-colors hover:border-blue-400 hover:bg-blue-100 hover:text-blue-700"
                        >
                          <TimerReset size={18} />
                        </button>
                      )}
                      {forceLogoutOption && u._id !== userInfo?.id && (
                        <button
                          onClick={() => { setSelectedUserForDelete(u); setDeleteModal(true); }}
                          title={`Delete ${u.name}`}
                          aria-label={`Delete ${u.name}`}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-red-200 text-red-600 hover:border-red-400 hover:bg-red-50 hover:text-red-800 transition-colors"
                        >
                          <Trash2 size={18} />
                        </button>
                      )}
                      {agentResetOption && u.merchantId && (
                        <button
                          onClick={() => { setSelectedUserForAgentReset(u); setAgentResetModal(true); }}
                          title={`Reset Agent Program progress for ${u.name}`}
                          aria-label={`Reset Agent Program progress for ${u.name}`}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-orange-200 bg-orange-50 text-orange-600 transition-colors hover:border-orange-400 hover:bg-orange-100 hover:text-orange-800"
                        >
                          <Eraser size={18} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filtered.length > 0 && (
          <div className="flex flex-col gap-3 px-4 py-3 border-t border-gray-200 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <span className="text-sm text-gray-500">{filtered.length} users</span>
              <label className="flex items-center gap-2 text-sm text-gray-600">
                Rows
                <select value={rowsPerPage} onChange={handleRowsPerPage} className="border border-gray-300 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
                  {[10, 20, 50, 100].map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </label>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage(p => p - 1)} disabled={page <= 1}
                className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors">Prev</button>
              <span className="text-sm text-gray-600 px-1">{page} / {totalPages}</span>
              <button onClick={() => setPage(p => p + 1)} disabled={page >= totalPages}
                className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors">Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Add User Modal */}
      <Modal open={openModal} onClose={() => setOpenModal(false)} title="Add User"
        footer={
          <>
            <button onClick={() => setOpenModal(false)} className={btnOutline}>Cancel</button>
            <button onClick={addUserHandler} disabled={loadingAddUserInfo} className={btnPrimary}>
              {loadingAddUserInfo ? 'Adding…' : 'Add User'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <Input name="Name *" value={name} onChange={e => setName(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Identifier *</label>
              <Input name="Identifier *" value={identifier} onChange={e => setIdentifier(e.target.value)} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
            <Input name="Email *" type="email" value={email} onChange={e => setEmail(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
            <Select name="Role *" value={role} onChange={e => setRole(e.target.value)} options={roleOptions} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">IP Address *</label>
            <Input name="IP Address *" value={ipAddress} onChange={e => setIpAddress(e.target.value)} />
          </div>
          <p className="text-xs text-gray-400">All fields marked * are mandatory</p>
        </div>
      </Modal>

      {/* Password Modal */}
      <Modal open={passwordModal} onClose={() => { setPasswordModal(false); setNewPassword(''); setTimeout(() => dispatch(getUsers()), 1000); }} title="New Password"
        footer={
          <button onClick={() => { setPasswordModal(false); setNewPassword(''); setTimeout(() => dispatch(getUsers()), 1000); }} className={btnOutline}>Close</button>
        }
      >
        <div>
          <p className="text-sm text-gray-600 mb-3">User created successfully. Save this password:</p>
          <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 font-mono text-sm text-gray-800 select-all">{newPassword}</div>
        </div>
      </Modal>

      {/* Force Logout Single */}
      <Modal open={forceLogoutModal && !!selectedUserForLogout} onClose={() => { setForceLogoutModal(false); setSelectedUserForLogout(null); }} title="Force Logout User"
        footer={
          <>
            <button onClick={() => { setForceLogoutModal(false); setSelectedUserForLogout(null); }} className={btnOutline}>Cancel</button>
            <button onClick={() => dispatch(forceLogoutUser(selectedUserForLogout._id))} disabled={loadingForceLogoutUser} className={btnDanger}>
              {loadingForceLogoutUser ? 'Logging out…' : 'Force Logout'}
            </button>
          </>
        }
      >
        <p className="text-sm text-gray-600">
          Force logout <strong>{selectedUserForLogout?.name}</strong>? This will invalidate all their active sessions.
        </p>
      </Modal>

      {/* Delete User */}
      <Modal open={deleteModal && !!selectedUserForDelete} onClose={() => { setDeleteModal(false); setSelectedUserForDelete(null); }} title="Delete User"
        footer={
          <>
            <button onClick={() => { setDeleteModal(false); setSelectedUserForDelete(null); }} className={btnOutline}>Cancel</button>
            <button onClick={() => dispatch(deleteUser(selectedUserForDelete._id))} disabled={loadingDeleteUser} className={btnDanger}>
              {loadingDeleteUser ? 'Deleting...' : 'Delete User'}
            </button>
          </>
        }
      >
        <p className="text-sm text-gray-600">
          Delete <strong>{selectedUserForDelete?.name}</strong>? This action cannot be undone.
        </p>
      </Modal>

      {/* Reset Agent Program Progress */}
      <Modal
        open={agentResetModal && !!selectedUserForAgentReset}
        onClose={() => { if (!resettingAgent) { setAgentResetModal(false); setSelectedUserForAgentReset(null); } }}
        title="Reset Agent Program Progress"
        footer={
          <>
            <button
              onClick={() => { setAgentResetModal(false); setSelectedUserForAgentReset(null); }}
              disabled={resettingAgent}
              className={btnOutline}
            >
              Cancel
            </button>
            <button onClick={confirmAgentReset} disabled={resettingAgent} className={btnDanger}>
              {resettingAgent ? 'Resetting…' : 'Yes, reset everything'}
            </button>
          </>
        }
      >
        <p className="text-sm text-gray-800 font-medium">
          Permanently wipe all Agent Program data for <strong>{selectedUserForAgentReset?.name}</strong>?
        </p>
        <p className="text-sm text-gray-600 mt-2">
          This deletes all their leads, points, level, transactions, held/available balances, and vouchers —
          they go back to Level 0 with ₹0 balance, as if brand new. This does not affect their user account,
          KYC, or loan enquiries. <strong>This cannot be undone.</strong>
        </p>
      </Modal>

      {/* Bulk Force Logout */}
      <Modal open={bulkLogoutModal} onClose={() => setBulkLogoutModal(false)} title="Force Logout Multiple Users"
        footer={
          <>
            <button onClick={() => setBulkLogoutModal(false)} className={btnOutline}>Cancel</button>
            <button onClick={() => dispatch(forceLogoutMultipleUsers(selectedRows.map(u => u._id)))} disabled={loadingForceLogoutMultiple} className={btnDanger}>
              {loadingForceLogoutMultiple ? 'Logging out…' : `Force Logout (${selectedRows.length})`}
            </button>
          </>
        }
      >
        <p className="text-sm text-gray-600 mb-3">Force logout <strong>{selectedRows.length}</strong> selected users?</p>
        <div className="max-h-32 overflow-y-auto space-y-1">
          {selectedRows.map(u => <p key={u._id} className="text-xs text-gray-500">• {u.name} ({u.email})</p>)}
        </div>
      </Modal>

      {/* Assign Lead Modal (single user) */}
      <Modal
        open={assignModal}
        onClose={() => { setAssignModal(false); setAssignTarget(null); setAssignLeadId('') }}
        title={`Assign Lead — ${assignTarget?.name || ''}`}
        footer={
          <>
            <button onClick={() => { setAssignModal(false); setAssignTarget(null); setAssignLeadId('') }} className={btnOutline}>Cancel</button>
            <button onClick={handleAssignLead} disabled={assignLoading} className={btnPrimary}>
              {assignLoading ? 'Saving…' : 'Save'}
            </button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-600">
            Select the lead user that <strong>{assignTarget?.name}</strong> will report to. Their caller records will be visible to the assigned lead.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Lead User</label>
            <select
              value={assignLeadId}
              onChange={e => setAssignLeadId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="">— No Lead (unassign) —</option>
              {data
                .filter(u => u._id !== assignTarget?._id)
                .map(u => (
                  <option key={u._id} value={u._id}>{u.name} ({u.role?.name || 'N/A'})</option>
                ))
              }
            </select>
          </div>
        </div>
      </Modal>

      {/* Bulk Assign Lead Modal */}
      <Modal
        open={bulkAssignModal}
        onClose={() => { setBulkAssignModal(false); setBulkAssignLeadId('') }}
        title={`Assign Lead — ${selectedRows.length} user(s)`}
        footer={
          <>
            <button onClick={() => { setBulkAssignModal(false); setBulkAssignLeadId('') }} className={btnOutline}>Cancel</button>
            <button onClick={handleBulkAssignLead} disabled={bulkAssignLoading} className={btnPrimary}>
              {bulkAssignLoading ? 'Saving…' : `Assign to ${selectedRows.length} user(s)`}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Lead User</label>
            <select
              value={bulkAssignLeadId}
              onChange={e => setBulkAssignLeadId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="">— No Lead (unassign) —</option>
              {data
                .filter(u => !selectedRows.find(r => r._id === u._id))
                .map(u => (
                  <option key={u._id} value={u._id}>{u.name} ({u.role?.name || 'N/A'})</option>
                ))
              }
            </select>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">Selected users ({selectedRows.length}):</p>
            <div className="max-h-32 overflow-y-auto space-y-1 bg-gray-50 rounded-lg p-2">
              {selectedRows.map(u => (
                <p key={u._id} className="text-xs text-gray-600">• {u.name} <span className="text-gray-400">({u.role?.name || 'N/A'})</span></p>
              ))}
            </div>
          </div>
        </div>
      </Modal>

      {/* Reset Login Rate Limit */}
      <Modal open={rateLimitModal && !!selectedUserForRateLimit} onClose={() => { setRateLimitModal(false); setSelectedUserForRateLimit(null); }} title="Reset Login Rate Limit"
        footer={
          <>
            <button onClick={() => { setRateLimitModal(false); setSelectedUserForRateLimit(null); }} className={btnOutline}>Cancel</button>
            <button onClick={() => dispatch(resetLoginRateLimit(selectedUserForRateLimit._id))} disabled={loadingResetRateLimit} className={btnPrimary}>
              {loadingResetRateLimit ? 'Resetting…' : 'Reset Rate Limit'}
            </button>
          </>
        }
      >
        <p className="text-sm text-gray-600">
          Clear the failed login lockout for <strong>{selectedUserForRateLimit?.name}</strong>? They will be able to attempt login again immediately instead of waiting out the 15-minute window.
        </p>
      </Modal>
    </div>
  )
}

export default UserList
